
import React, { useState } from 'react';
import SEO from '../SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Activity, BarChart2, CheckCircle, Check, X, ChevronDown, ChevronUp, Globe, MousePointer, Shield, TrendingUp, Zap, Target, Layout, Clock, Play, Server, Lock, Code , Star} from 'lucide-react';

interface LandingPageProps {
    onLogin?: () => void;
    onNavigate?: (page: string) => void;
}

const RoOrganicWebsiteTraffic: React.FC<LandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);

    // Schema.org Structured Data
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Generator de trafic organic pentru website",
            "description": "Serviciu premium de trafic organic pentru îmbunătățirea clasărilor SEO și manipularea CTR cu adrese IP rezidențiale reale.",
            "brand": { "@type": "Brand", "name": "Traffic Creator" }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Acasă", "item": "https://traffic-creator.com" },
                { "@type": "ListItem", "position": 2, "name": "Trafic organic pentru website", "item": "https://traffic-creator.com/ro/organic-website-traffic" }
            ]
        }
    ];

    const benefits = [
        {
            icon: <Search className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Crește CTR-ul organic",
            desc: "Vizitatorii noștri caută cuvintele tale cheie și fac clic pe rezultatul tău, semnalând o relevanță ridicată algoritmilor de căutare."
        },
        {
            icon: <Clock className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Crește timpul de ședere",
            desc: "Vizitatorii rămân pe site-ul tău câte minute, derulează prin conținut și interacționează, reducând drastic rata de respingere."
        },
        {
            icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Dominare SEO locală",
            desc: "Direcționează traficul din orașe sau regiuni specifice pentru a te clasa mai sus în rezultatele căutării locale și pe hărți."
        },
        {
            icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />,
            title: "100% comportament uman",
            desc: "Mișcările mouse-ului, derularea și clicurile sunt randomizate pentru a imita perfect interacțiunea umană reală."
        }
    ];

    const comparisons = [
        { feature: "Sursă de trafic", us: "IP-uri rezidențiale reale", them: "Proxy-uri de centre de date" },
        { feature: "Comportament", us: "Derulare, clicuri, citire", them: "Accesări statice" },
        { feature: "Google Analytics", us: "Recunoscut 100%", them: "Filtrat frecvent / Bot" },
        { feature: "Efect asupra SEO", us: "Semnal pozitiv de clasare", them: "Neutru sau negativ" },
        { feature: "Sigur pentru AdSense", us: "Da, verificat", risc: "Risc ridicat de interzicere" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden">
            <SEO
                title="Cumpără trafic organic pentru website"
                description="Obține trafic organic real 100% de la adrese IP rezidențiale în 2026. Botul nostru avansat de trafic AI simulează comportamentul uman (derulare, clic, citire) pentru a îmbunătăți clasările SERP și metricile de implicare."
                keywords="trafic organic website 2026, cumpără trafic organic, trafic seo, manipulare ctr, îmbunătățire clasări, vizitatori reali website, bot trafic ai"
                schema={schema}
                type="product"
            />

            {/* Hreflang Tags */}

            {/* Navigation */}
            <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-gray-100/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/" className="flex items-center gap-2">
                            <span className="text-2xl font-black text-[#ff5a0a] tracking-tight group-hover:scale-105 transition-transform">TRAFFIC</span>
                            <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-8">
                        <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Cum funcționează
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <a href="#benefits" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Funcții
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Prețuri
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </Link>
                        </div>
                        <Link to="/login" className="bg-black text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#ff5a0a] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center rounded-sm">
                            Autentificare în panou
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-32 px-6 relative overflow-hidden bg-white">
                {/* Background Decor */}
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none rounded-bl-[100px]"></div>
                <div className="absolute top-40 left-10 w-4 h-4 rounded-full bg-orange-200 animate-pulse"></div>
                <div className="absolute top-60 right-20 w-3 h-3 rounded-full bg-blue-200 animate-bounce delay-700"></div>

                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-widest mb-8 rounded-full shadow-sm hover:shadow-md transition-shadow cursor-default">
                            <Zap size={14} className="fill-[#ff5a0a]" /> Motor de inteligență SEO
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] mb-8 text-gray-900">
                            DOMINĂ <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">REZULTATELE</span><br />CĂUTĂRII ORGANICE.
                        </h1>
                        <p className="text-xl text-gray-500 mb-10 max-w-lg leading-relaxed font-medium">
                            Singurul generator de trafic organic pentru website conceput pentru profesioniștii SEO. Manipulează CTR-ul, crește timpul de ședere și transmite semnale de autoritate către Google.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup" className="group bg-[#ff5a0a] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-black transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                                Pornește campania SEO
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a href="#demo" className="bg-white border border-gray-200 text-gray-900 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-[#ff5a0a] hover:text-[#ff5a0a] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 rounded-sm group">
                                <Play size={16} className="group-hover:fill-[#ff5a0a]" /> Vezi demonstrația live
                            </a>
                        </div>

                        <div className="mt-8 flex items-center gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <span>Folosit de 15.000+ agenții SEO</span>
                            <div className="flex -space-x-2">
                                <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white"></div>
                                <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white"></div>
                                <div className="w-6 h-6 rounded-full bg-gray-400 border-2 border-white"></div>
                            </div>
                        </div>
                    </div>

                    {/* Visual: Abstract SEO Graph */}
                    <div className="relative h-[550px] perspective-1000 group">
                        {/* Card Reflection */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-gray-100 to-gray-50 transform rotate-6 scale-95 opacity-50 rounded-2xl border border-gray-100 transition-transform duration-700 group-hover:rotate-3 group-hover:scale-95"></div>

                        {/* Main Interface Card */}
                        <div className="absolute inset-0 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 overflow-hidden transform transition-transform duration-700 group-hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
                            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 pointer-events-none"></div>

                            {/* Fake Browser Header */}
                            <div className="border-b border-gray-100 pb-4 mb-6 flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                                <div className="ml-6 bg-gray-100 rounded-lg h-5 w-64 flex items-center px-2">
                                    <Lock size={10} className="text-gray-400 mr-2" />
                                    <div className="w-32 h-2 bg-gray-200 rounded-sm"></div>
                                </div>
                            </div>

                            {/* SERP Simulation */}
                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={`p-5 rounded-lg border transition-all duration-500 hover:shadow-lg cursor-default relative overflow-hidden ${i === 1 ? 'border-[#ff5a0a]/20 bg-orange-50/30' : 'border-gray-50 bg-white'}`}>

                                        {i === 1 && <div className="absolute top-0 left-0 w-1 h-full bg-[#ff5a0a]"></div>}

                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                                                {i === 1 ? 'TC' : 'Ad'}
                                            </div>
                                            <div className="flex-1">
                                                <div className="w-32 h-2 bg-gray-200 rounded-sm mb-1"></div>
                                                <div className="w-48 h-2 bg-gray-100 rounded-sm"></div>
                                            </div>
                                        </div>

                                        <div className={`w-3/4 h-5 rounded-sm mb-3 ${i === 1 ? 'bg-gradient-to-r from-[#ff5a0a] to-orange-400' : 'bg-blue-100'}`}></div>
                                        <div className="space-y-2">
                                            <div className="w-full h-2 bg-gray-100 rounded-sm"></div>
                                            <div className="w-5/6 h-2 bg-gray-100 rounded-sm"></div>
                                        </div>

                                        {i === 1 && (
                                            <div className="mt-4 flex items-center gap-2 text-[#ff5a0a] text-xs font-bold uppercase tracking-wider animate-pulse">
                                                <TrendingUp size={12} /> Traffic quality • CTR analysis active
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Floating Stats Badge */}
                            <div className="absolute bottom-6 right-6 bg-gray-900/95 backdrop-blur-md text-white p-5 rounded-xl shadow-2xl z-20 border border-gray-800 animate-in fade-in slide-in-from-bottom-4 delay-300">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a0a] mb-2 flex items-center gap-2">
                                    <Activity size={12} /> Performanță
                                </div>
                                <div className="text-4xl font-black mb-1">18.4%</div>
                                <div className="text-xs text-gray-400 font-medium">CTR organic (7 zile)</div>
                                <div className="w-full bg-gray-800 h-1 mt-3 rounded-full overflow-hidden">
                                    <div className="w-[70%] h-full bg-[#ff5a0a]"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h2 className="text-4xl font-black uppercase tracking-tight mb-6">De ce să fii pe poziția #1?</h2>
                        <div className="w-16 h-1.5 bg-[#ff5a0a] mx-auto mb-8 rounded-full"></div>
                        <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
                            Algoritmii de căutare evoluează. Backlink-urile nu mai sunt suficiente. Ai nevoie de <span className="text-gray-900 font-bold">semnale ale utilizatorilor</span>. Traffic Creator generează timpul de ședere perfect, adâncimea derulării și metricile de interacțiune.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {benefits.map((b, i) => (
                            <div key={i} className="flex gap-8 p-10 border border-gray-100 hover:border-[#ff5a0a]/30 bg-white hover:bg-orange-50/20 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-0 bg-[#ff5a0a] group-hover:h-full transition-all duration-300"></div>

                                <div className="shrink-0">
                                    <div className="bg-orange-50 w-16 h-16 flex items-center justify-center rounded-2xl group-hover:bg-[#ff5a0a] group-hover:rotate-6 transition-all duration-300 text-[#ff5a0a] group-hover:text-white shadow-sm">
                                        {b.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#ff5a0a] transition-colors">{b.title}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">
                                        {b.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technical Detail / Dark Mode Section */}
            <section className="py-32 px-6 bg-[#f9fafb] border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-20 items-center">
                        <div className="space-y-12">
                            <div className="pl-8 border-l-2 border-gray-200 hover:border-[#ff5a0a] transition-colors duration-300">
                                <h3 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3">
                                    <Target className="text-[#ff5a0a]" size={24} /> Vizare pe cuvinte cheie
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed font-medium">
                                    Nu trimite doar trafic către un URL. Sistemul nostru imită un utilizator care caută cuvintele tale cheie specifice pe Google, găsește rezultatul tău și face clic pe el. Acesta este semnalul suprem pentru relevanță.
                                </p>
                            </div>
                            <div className="pl-8 border-l-2 border-gray-200 hover:border-[#ff5a0a] transition-colors duration-300">
                                <h3 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3">
                                    <Layout className="text-[#ff5a0a]" size={24} /> Călătorii complexe
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed font-medium">
                                    Definește căi cu mai multe pagini. Vizitatorii ajung pe articolul tău de blog, citesc 60 de secunde, fac clic pe pagina de prețuri și apoi vizitează formularul de contact.
                                </p>
                            </div>
                        </div>

                        {/* Code Block Visual */}
                        <div className="bg-[#111] text-gray-300 p-2 rounded-xl shadow-2xl relative overflow-hidden group">
                            {/* Window Actions */}
                            <div className="flex items-center gap-2 p-4 border-b border-gray-800 bg-[#151515] rounded-t-lg">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                <div className="text-xs font-mono text-gray-500 ml-4 flex items-center gap-2">
                                    <Code size={12} /> campaign_config.json
                                </div>
                            </div>

                            <div className="p-8 font-mono text-xs md:text-sm leading-relaxed overflow-x-auto">
                                <div className="opacity-50 mb-4 text-[10px] uppercase tracking-widest">// Configurare trafic</div>
                                <div><span className="text-purple-400">const</span> <span className="text-blue-400">campaign</span> = {'{'}</div>
                                <div className="pl-4">
                                    <span className="text-gray-400">target:</span> <span className="text-green-400">"Primele 3 poziții"</span>,
                                </div>
                                <div className="pl-4">
                                    <span className="text-gray-400">keywords:</span> [
                                </div>
                                <div className="pl-8 text-orange-300">"servicii de îmbunătățire a clasării",</div>
                                <div className="pl-8 text-orange-300">"cumpără trafic organic",</div>
                                <div className="pl-8 text-orange-300">"manipulare ctr seo"</div>
                                <div className="pl-4">],</div>
                                <div className="pl-4">
                                    <span className="text-gray-400">behavior:</span> {'{'}
                                </div>
                                <div className="pl-8">
                                    <span className="text-blue-300">timeOnPage:</span> <span className="text-yellow-300">"185s"</span>,
                                </div>
                                <div className="pl-8">
                                    <span className="text-blue-300">scrollDepth:</span> <span className="text-yellow-300">"85%"</span>,
                                </div>
                                <div className="pl-8">
                                    <span className="text-blue-300">bounceRate:</span> <span className="text-yellow-300">"12%"</span>
                                </div>
                                <div className="pl-4">{'}'}</div>
                                <div>{'}'};</div>

                                <div className="mt-6 flex items-center gap-2 text-green-500 text-[10px] font-bold uppercase tracking-widest">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> compilator pregătit
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Premium Comparison */}
            <section className="py-32 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-4">Calitatea contează</h2>
                        <p className="text-gray-500">Nu risca autoritatea domeniului tău cu trafic de boți ieftin.</p>
                    </div>

                    <div className="bg-white shadow-[0_10px_60px_rgba(0,0,0,0.05)] rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-900 text-white p-6 text-xs font-bold uppercase tracking-widest">
                            <div className="pl-4">Funcție</div>
                            <div className="text-[#ff5a0a] flex items-center gap-2"><Zap size={14} className="fill-[#ff5a0a]" /> Traffic Creator</div>
                            <div className="text-gray-500">Boți ieftini</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {comparisons.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 p-6 items-center text-sm font-medium hover:bg-gray-50 transition-colors group">
                                    <div className="font-bold text-gray-900 flex items-center gap-3 pl-4">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-[#ff5a0a] transition-colors"></div>
                                        {c.feature}
                                    </div>
                                    <div className="flex items-center gap-3 text-[#ff5a0a] font-bold bg-orange-50/50 -my-6 py-6 border-x border-orange-100/50">
                                        <CheckCircle size={18} className="ml-4" /> {c.us}
                                    </div>
                                    <div className="text-gray-400 flex items-center gap-3 pl-4">
                                        <X size={16} /> {c.them || (c as any).risc}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">Testimoniale</span>
                        <h2 className="text-4xl font-black text-gray-900 mb-6">Povești de succes</h2>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                            Descoperă cum profesioniștii domină SERP-urile cu traficul nostru organic.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=andrei_p`} alt="Andrei Popescu" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Andrei Popescu</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Director agenție SEO</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">„Clienții noștri cer rezultate, iar manipularea CTR de la TrafficCreator este arma secretă pe care o folosim pentru a le obține. Sigură, de încredere și incredibil de eficientă."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=elena_m`} alt="Elena Marinescu" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Elena Marinescu</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Creator de site-uri de nișă</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">„Eram sceptică cu privire la traficul automatizat, dar IP-urile rezidențiale și călătoriile utilizatorilor complexe de aici sunt complet diferite. Imită perfect căutarea organică reală."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=mihai_i`} alt="Mihai Ionescu" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Mihai Ionescu</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Consultant marketing digital</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">„Am înlocuit întreaga noastră rețea PBN cu semnale de trafic direcționate. Este mai sigur, necesită mai puțină întreținere și oferă metrici mai bune pentru timpul de ședere."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-8 text-white">Începe să te clasezi acum</h2>
                    <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
                        Concurenții tăi o fac deja. Egalizează condițiile cu cel mai avansat generator de trafic organic din lume.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link to="/signup" className="bg-white text-black px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                            Pornește perioada gratuită <ArrowRight size={18} />
                        </Link>
                        <Link to="/#pricing" className="bg-transparent border-2 border-white text-white px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-[#ff5a0a] transition-all rounded-sm flex items-center justify-center">
                            Vezi prețurile
                        </Link>
                    </div>
                    <div className="mt-12 text-white/60 text-xs font-bold uppercase tracking-widest">
                        Nu este necesar card de credit pentru perioada de probă • 100% securizat • Anulare oricând
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black text-white py-20 px-6 border-t border-gray-900">
                <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
                    <div className="col-span-2">
                        <div className="text-3xl font-black text-white tracking-tight mb-6">TRAFFIC CREATOR</div>
                        <p className="text-gray-500 max-w-sm mb-8">
                            Standardul industriei pentru generarea traficului SEO. Ajutăm agențiile și proprietarii de site-uri să îmbunătățească în siguranță semnalele de vizibilitate în căutare.
                        </p>
                        <div className="flex gap-4">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-gray-500 hover:bg-[#ff5a0a] hover:text-white transition-colors cursor-pointer">
                                    <Globe size={16} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">Platformă</h4>
                        <ul className="space-y-4 text-sm font-medium text-gray-300">
                            <li><Link to="/#workflow" className="hover:text-[#ff5a0a] transition-colors">Cum funcționează</Link></li>
                            <li><Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors">Prețuri</Link></li>
                            <li><Link to="/login" className="hover:text-[#ff5a0a] transition-colors">Panou de control</Link></li>
                            <li><Link to="/dashboard/affiliate" className="hover:text-[#ff5a0a] transition-colors">Program de afiliere</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">Companie</h4>
                        <ul className="space-y-4 text-sm font-medium text-gray-300">
                            <li><Link to="/about" className="hover:text-[#ff5a0a] transition-colors">Despre noi</Link></li>
                            <li><Link to="/blog" className="hover:text-[#ff5a0a] transition-colors">Blog SEO</Link></li>
                            <li><Link to="/helpdesk" className="hover:text-[#ff5a0a] transition-colors">Centru de ajutor</Link></li>
                            <li><Link to="/legal/privacy" className="hover:text-[#ff5a0a] transition-colors">Politica de confidențialitate</Link></li>
                            <li><Link to="/legal/terms" className="hover:text-[#ff5a0a] transition-colors">Termeni și condiții</Link></li>
                            <li><Link to="/legal/impressum" className="hover:text-[#ff5a0a] transition-colors">Impressum</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-gray-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                    <div>© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</div>
                    <div className="flex gap-4 text-gray-500">
                        <Link to="/buy-website-traffic" className="hover:text-[#ff5a0a] transition-colors">Cumpără trafic</Link>
                        <Link to="/organic-website-traffic" className="text-[#ff5a0a]">Trafic organic</Link>
                        <Link to="/free-website-traffic" className="hover:text-[#ff5a0a] transition-colors">Trafic gratuit</Link>
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span>Sisteme operaționale</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default RoOrganicWebsiteTraffic;
